import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../auth.service';
import { LoaderService } from '../../loader.service';
import { AlertDialogService } from '../../shared/alert-dialog/alert-dialog.service';
import { ConfirmDialogService } from '../../shared/confirm-dialog/confirm-dialog.service';
import { SubscriptionService } from '../subscription.service';
import { AuthzAccessHintService } from '../../shared/authz-access-hint/authz-access-hint.service';
import { AUTHZ_FEATURES } from '../../constants/permissions';
import { permissionsForFeature } from '../../utils/permission.utils';

@Component({
  selector: 'app-config-request-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.css']
})
export class ConfigRequestViewComponent implements OnInit {
  public kind = 'subscription';
  public approvalPermission = '';
  public feature = 'subscription';
  public canDecide = false;
  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public isOwnRequest = false;
  public request: any = null;
  public rule: any = {};
  public loadError = '';
  private raw: any = null;
  private userEmail = '';

  constructor(
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private authService: AuthService,
    private loaderService: LoaderService,
    private dialogService: ConfirmDialogService,
    private alertDialogService: AlertDialogService,
    private authzAccessHintService: AuthzAccessHintService,
  ) {}

  async ngOnInit() {
    const data = this.route.snapshot.data || {};
    this.kind = data.kind || 'subscription';
    this.feature = data.feature || (this.kind === 'billingPlan' ? AUTHZ_FEATURES.BILLING_PLANS : AUTHZ_FEATURES.SUBSCRIPTION);
    const permissions = permissionsForFeature(this.feature, this.authService.getAuthzPanels()) || {};
    this.approvalPermission = permissions.approve || '';
    this.canDecide = this.authService.isAuthorizedFor(this.approvalPermission, this.feature);
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData(this.feature);
    const user: any = await this.authService.getUserDetails().toPromise();
    this.userEmail = String((user && user.email) || '').trim().toLowerCase();
    await this.load();
  }

  get isPending() {
    return this.request && String(this.request.requestStatus).toUpperCase() === 'PENDING';
  }

  get showApprovedBy() {
    if (!this.request) {
      return false;
    }
    const status = String(this.request.requestStatus || '').toUpperCase();
    return status === 'APPROVED' || status === 'REJECTED';
  }

  get approvedByLabel() {
    const status = String((this.request && this.request.requestStatus) || '').toUpperCase();
    return status === 'REJECTED' ? 'Rejected By' : 'Approved By';
  }

  get title() {
    if (this.kind === 'billingPlan') {
      return `ACCESS FEE REQUEST: ${(this.request && this.request.name) || ''}`;
    }
    return `SUBSCRIPTION REQUEST: ${(this.request && this.request.name) || ''}`;
  }

  displayOrNA(value) {
    return value == null || value === '' ? '--' : value;
  }

  decide(action: string) {
    if (action === 'approve' && this.isOwnRequest) {
      return;
    }
    const label = action === 'approve' ? 'Approve' : 'Reject';
    this.dialogService.open({
      title: label,
      message: `${label} this request?`,
      cancelText: 'Cancel',
      confirmText: label
    });
    this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.loaderService.openLoading();
      this.subscriptionService.decideConfigRequest(this.request._id, action, this.kind).subscribe(
        (response: any) => {
          this.loaderService.closeLoading();
          const successMessage = action === 'reject'
            ? 'Request rejected successfully'
            : ((response && response.message) || 'Request approved successfully');
          this.alert(label, successMessage);
          const status = action === 'approve' ? 'APPROVED' : 'REJECTED';
          if (this.raw) {
            this.raw.requestStatus = status;
          }
          if (this.request) {
            this.request.requestStatus = status;
          }
        },
        error => {
          this.loaderService.closeLoading();
          this.alert(label, this.errorMessage(error));
        }
      );
    });
  }

  private async load() {
    const requestId = this.route.snapshot.paramMap.get('id');
    this.loaderService.openLoading();
    try {
      this.raw = await this.subscriptionService.getConfigRequestById(requestId, this.kind).toPromise();
      this.request = this.raw;
      this.rule = (this.request && this.request.entity) || {};
      this.isOwnRequest = this.isCreatedByCurrentUser(this.request && this.request.createdBy);
      this.loadError = '';
    } catch (error) {
      this.request = null;
      this.raw = null;
      this.loadError = this.errorMessage(error);
    }
    this.loaderService.closeLoading();
  }

  private isCreatedByCurrentUser(createdBy: string) {
    if (!this.userEmail || !createdBy) {
      return false;
    }
    return String(createdBy).trim().toLowerCase() === this.userEmail;
  }

  private errorMessage(error) {
    const body = error && error.error;
    if (body && (body.message || body.errorMessage)) {
      return body.message || body.errorMessage;
    }
    return 'Request failed';
  }

  private alert(title: string, message: string) {
    this.alertDialogService.open({ title, message, confirmText: 'OK' });
  }
}
